import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Quote, ArrowRight, X, Instagram, Mail, PhoneIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Home = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = [
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/e958351f-ff02-4c69-979a-f414b519cbd3.jpeg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/f4805d42-d514-4e4a-8b83-78a790091ff8.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/9687f480-5ae3-4b86-837c-7d72243956e2.png",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/157306b2-a140-4542-a118-44b81f930a0c.png",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/bbc4c954-66ad-4203-a087-46b423f15526.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/470fffa2-c922-46e6-9910-0667448fe3d8.png",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/43988306-1914-460b-a426-ad52b4e3b933.png",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/6b505f9a-eb61-4f83-9949-82dc8d8ed26a.jpeg"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  const galleryImages = [
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/9b699e10-343e-45a4-b83d-ff2b0cf09fca.JPG",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/b5e62df5-2752-499f-8fed-f46b7f8eb0bc.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/191cdcf8-f070-496c-9483-984e185b9b4d.jpeg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/cfc2afb0-6c9f-4dfe-92b3-b9c227b7870d.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/a27c7360-7528-4a9e-9b00-8dbcf7e05a90.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/806b2d85-5f88-43cb-99c1-4b55dcbe495b.jpg"
  ];

  return (
    <div className="flex flex-col">
      <Helmet>
        <title>Toftes Hundesalon v/Helene Tofte Botin Christensen</title>
        <meta name="description" content="Professionel hundepleje i Tranbjerg J. Vi tilbyder klipning, vask og forkælelse af din hund i trygge rammer." />
        <meta property="og:title" content="Toftes Hundesalon" />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/751ef333-fff5-4650-af56-db08ecfee4bf.png" />
      </Helmet>

      {/* Hero Section - Inspired by Nicepage Layout */}
      <section className="relative min-h-[125vh] sm:min-h-[110vh] md:min-h-[90vh] flex items-start md:items-center overflow-hidden bg-stone-50 px-4 pt-24 md:pt-0">
        {/* Background Image Slideshow */}
        <div className="absolute inset-0 w-full h-full">
          <AnimatePresence>
            <motion.img 
              key={currentImageIndex}
              src={heroImages[currentImageIndex]} 
              alt="Toftes Hundesalon Hero" 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2, ease: "easeInOut" }}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-black/10 md:bg-gradient-to-r md:from-black/30 md:to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto w-full relative z-10 flex flex-col md:flex-row items-start md:items-center justify-start h-full">
          {/* Left-Aligned Typographic Block */}
          <div className="md:w-1/2 min-[1200px]:w-full text-left flex flex-col items-start">
            <motion.h1 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl md:text-5xl min-[1200px]:text-3xl font-serif italic text-white [text-shadow:_0_4px_15px_rgba(0,0,0,0.6)] mb-8 max-w-[1000px] leading-tight"
            >
              <span className="min-[1200px]:whitespace-nowrap">Hos Toftes Hundesalon er din hund i fokus fra start til slut.</span>{' '}
              <br className="hidden min-[1200px]:block" />
              <span className="min-[1200px]:whitespace-nowrap">En velplejet hund er en glad hund.</span>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col gap-6 items-start"
            >
              <a 
                href="https://tofteshundesalon.planway.com/" 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-taupe-500 hover:bg-taupe-600 text-white px-10 py-4 rounded-none text-sm uppercase tracking-widest font-bold transition-all inline-flex items-center gap-2"
              >
                Book tid nu
              </a>
            </motion.div>
          </div>
        </div>

        {/* Overlapping "Follow Us" Card */}
        <div className="absolute bottom-10 right-4 md:right-20 z-20">
          <div className="bg-stone-100 text-stone-900 p-8 md:p-12 max-w-xs md:max-w-sm rounded-none shadow-2xl border border-stone-200">
            <h3 className="text-xs uppercase tracking-[0.3em] font-bold mb-6 text-stone-700">Følg os</h3>
            <p className="text-sm italic mb-8 leading-relaxed text-stone-600">
              Følg med i hverdagen i salonen og se de mange glade hunde, der kigger forbi Tranbjerg.
            </p>
            <div className="flex gap-6">
              <a href="https://www.instagram.com/toftes_hundesalon" target="_blank" rel="noopener noreferrer" className="text-stone-800 hover:text-stone-600 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="mailto:helenetofte@hotmail.com" className="text-stone-800 hover:text-stone-600 transition-colors">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section - Overlapping Image Style */}
      <section className="py-24 px-4 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-16 items-center">
          <div className="md:w-1/2 relative">
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-stone-100 -z-10" />
            <img 
              src="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/90ed79d5-5483-47bd-9bf6-d395a9e92027.jpg" 
              alt="Helene i salonen" 
              className="w-full h-auto shadow-xl rounded-none transition-all duration-700"
            />
          </div>
          <div className="md:w-1/2">
            <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-6">Velkommen</h2>
            <h3 className="!text-[1.6rem] md:text-6xl font-serif italic text-stone-900 mb-8 leading-tight">
              Kærlig pleje i trygge rammer
            </h3>
            <div className="prose prose-lg text-gray-600 leading-relaxed">
              <p className="mb-6">
                “Hos Toftes Hundesalon, gør jeg alt for at din hund får den bedste pleje. Her er din hund i fokus fra start til slut.”
              </p>
              <p className="mb-8 italic">
                Jeg glæder mig til at byde dig og din hund velkommen i salonen i Tranbjerg.
              </p>
              <Link 
                to="/om-salonen" 
                className="inline-block border-b-2 border-stone-800 text-stone-900 font-bold uppercase text-xs tracking-widest pb-1 hover:text-stone-600 hover:border-stone-600 transition-all"
              >
                Læs mere om mig
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid - Clean & Spacious */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-4">Hvad vi tilbyder</h2>
            <h3 className="!text-[1.6rem] md:text-6xl font-serif italic text-stone-900">Vores ydelser</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { title: "Klipning", desc: "Professionel klipning tilpasset din hunds race og dine ønsker.", link: "/klipning" },
              { title: "Vask & Føn", desc: "En grundig vask med milde produkter efterfulgt af nænsom tørring.", link: "/klipning" },
              { title: "Negleklip", desc: "Sikker og hurtig klipning af negle for din hunds komfort.", link: "/priser" }
            ].map((service, idx) => (
              <div key={idx} className="bg-white p-12 shadow-sm hover:shadow-md transition-shadow border-t-4 border-stone-700">
                <h4 className="text-2xl font-serif italic text-stone-900 mb-4">{service.title}</h4>
                <p className="text-gray-600 mb-8 leading-relaxed">{service.desc}</p>
                <Link to={service.link} className="text-xs uppercase tracking-widest font-bold text-stone-700 hover:text-stone-600 transition-colors">
                  Læs mere
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview - Grid with Lightbox */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-4">Galleri</h2>
            <h3 className="!text-[1.6rem] md:text-6xl font-serif italic text-stone-900">Vores gæster</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.map((img, idx) => (
              <motion.div 
                key={idx} 
                whileHover={{ scale: 0.98 }}
                className="aspect-[4/5] overflow-hidden cursor-pointer"
                onClick={() => setSelectedImage(img)}
              >
                <img 
                  src={img} 
                  alt={`Galleri billede ${idx + 1}`} 
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                />
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-16">
            <Link 
              to="/galleri" 
              className="bg-stone-900 text-white px-12 py-4 rounded-none text-xs uppercase tracking-widest font-bold hover:bg-stone-800 transition-all"
            >
              Se hele galleriet
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          >
            <button 
              className="absolute top-6 right-6 text-white hover:text-stone-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-10 h-10" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage}
              alt="Forstørret billede"
              className="max-w-full max-h-full shadow-2xl object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Final CTA - Warm Beige */}
      <section className="py-32 px-4 bg-stone-100 text-stone-900 relative overflow-hidden border-t border-stone-200">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-stone-200/50 -skew-x-12 translate-x-1/2" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-sm uppercase tracking-[0.6em] font-bold mb-8 text-stone-600">Book din tid i dag</h2>
          <h3 className="!text-[1.6rem] md:text-7xl font-serif italic mb-12 leading-tight text-stone-900">
            Giv din hund <br /> den bedste oplevelse
          </h3>
          <div className="flex flex-col sm:flex-row justify-center gap-8">
            <a 
              href="https://tofteshundesalon.planway.com/" 
              target="_blank"
              rel="noopener noreferrer"
              className="bg-stone-800 text-white px-12 py-5 rounded-none font-bold uppercase text-xs tracking-widest hover:bg-stone-900 transition-all"
            >
              Book tid nu
            </a>
            <Link 
              to="/priser" 
              className="border-2 border-stone-800 text-stone-800 px-12 py-5 rounded-none font-bold uppercase text-xs tracking-widest hover:bg-stone-800 hover:text-white transition-all"
            >
              Se priser
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
